import React from "react";
import { Post } from "./types";

/**
 * Type for objects that have image URL fields (used for responsive image selection)
 */
export interface ImageUrlSource {
  imageUrl?: string;
  imageUrlMobile?: string;
  imageUrlCard?: string;
}

/**
 * Gets the appropriate image URL based on viewport width and usage context
 * @param post - The post object with image URLs (or any object with imageUrl fields)
 * @param context - 'card' for post cards (uses 320px), 'mobile' for mobile viewport (uses 480px), or 'full' for full image
 * @param viewportWidth - Current viewport width in pixels (optional, defaults to window.innerWidth)
 * @returns The appropriate image URL
 */
export const getResponsiveImageUrl = (
  post: ImageUrlSource | Post,
  context: "card" | "mobile" | "full" = "card",
  viewportWidth?: number
): string | undefined => {
  const width = viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : 0);
  const isMobile = width <= 768;

  // For card context: use 480px on mobile (≤768px), 320px on desktop (>768px)
  if (context === "card") {
    if (isMobile) {
      return post.imageUrlMobile || post.imageUrlCard || post.imageUrl;
    } else {
      return post.imageUrlCard || post.imageUrlMobile || post.imageUrl;
    }
  }

  // For mobile context or when viewport is ≤ 768px, use 480px version
  if (context === "mobile" || isMobile) {
    return post.imageUrlMobile || post.imageUrl;
  }

  // For full context or desktop, use original
  return post.imageUrl;
};

/**
 * React hook to get responsive image URL that updates on window resize
 * @param post - The post object with image URLs (or any object with imageUrl fields)
 * @param context - 'card' for post cards, 'mobile' for mobile viewport, or 'full' for full image
 * @returns The appropriate image URL
 */
export const useResponsiveImageUrl = (
  post: ImageUrlSource | Post,
  context: "card" | "mobile" | "full" = "card"
): string | undefined => {
  const [viewportWidth, setViewportWidth] = React.useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return getResponsiveImageUrl(post, context, viewportWidth);
};
